import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import { getIsDismissed } from './getIsDismissed.tsx';
import { get as rfoGet } from './rfoAnnouncementBannerApiService.tsx';
import { get as rfoUserDismissGet } from './rfoBannerDismissalUserProperty.tsx';
import type { RfoBannerSettingsResource } from './RfoBannerSettings.types.tsx';

/**
 * State for the RFO (Reason For Outage) announcement banner, written by the RFO micros service.
 *
 * Intentionally a separate resource `type` and a separate dismissal user property from
 * `ANNOUNCEMENT_BANNER_SETTINGS_V4`: the two banners are stored independently in the backend and
 * can both be enabled at once, so they must never share a cache entry or a dismissal record.
 *
 * `maxAge` is shorter than the admin banner's 15 minutes because RFO turns this banner off at the
 * end of an incident and the stale window is how long it lingers. `isCritical` matches every other
 * banner in the slot so the banner is server-rendered and does not shift the page down after
 * hydration.
 */
export const rfoAnnouncementBannerResource: RouteResource<RfoBannerSettingsResource> =
	createResource<RfoBannerSettingsResource>({
		type: 'RFO_ANNOUNCEMENT_BANNER',
		getKey: () => 'settings',
		getData: async (_, { tenantContext: { atlassianAccountId, isAnonymous } }) => {
			const [settings, hashIdLastDismissed] = await Promise.all([
				rfoGet(),
				rfoUserDismissGet(atlassianAccountId),
			]);

			return {
				...settings,
				isDismissable: isAnonymous ? false : settings?.isDismissable,
				isDismissed: getIsDismissed(isAnonymous, settings, hashIdLastDismissed),
			};
		},
		maxAge: 5 * 60 * 1000,
		isCritical: true,
	});

export const useRfoAnnouncementBannerResource =
	(): UseResourceHookResponse<RfoBannerSettingsResource> =>
		useResource<RfoBannerSettingsResource>(rfoAnnouncementBannerResource);
